import { httpsCallable } from "firebase/functions"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthState } from "react-firebase-hooks/auth"
import { auth, functions } from "@/lib/firebase"
import { BASE_URL } from "@/constants"
import { parseNdjsonStream } from "@/helpers/parseNdjsonStream"
import { appCheckHeaders } from "@/helpers/appCheckHeaders"

export async function syncPhases(projectTypeId) {
  if (!projectTypeId) throw new Error("Project Type ID is required")
  const syncPhasesCaller = httpsCallable(functions, "syncProjectTypePhaseList")
  const response = await syncPhasesCaller({ projectTypeId })
  if (!response.data) throw new Error("Failed to sync phases")
  return response.data
}

export function useProjectList(queryOptions) {
  const [user] = useAuthState(auth)
  const { enabled = true, ...restOptions } = queryOptions ?? {}
  return useQuery({
    queryKey: ["projectList"],
    queryFn: async ({ signal }) => {
      const idToken = await user.getIdToken()
      const response = await fetch(`${BASE_URL}/getprojects`, {
        method: "POST",
        headers: {
        ...(await appCheckHeaders()),
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ data: { hashtag: "" } }),
        signal
      })

      if (!response.ok) {
        const text = await response.text().catch(() => "")
        throw new Error(text || `Request failed with status ${response.status}`)
      }

      return parseNdjsonStream(response)
    },
    enabled: !!user && enabled,
    // IMPORTANT: do not retry — same reason as useTaskAnalytics (Filevine 429)
    retry: false,
    ...restOptions
  })
}

export function useProjectTypeList(queryOptions) {
  return useQuery({
    queryKey: ["projectTypeList"],
    queryFn: async () => {
      const projectTypeListCaller = httpsCallable(functions, "getprojecttypelist")
      const response = await projectTypeListCaller()

      if (!response.data?.items) {
        throw new Error("Failed to fetch project type list")
      }
      return response.data.items
    },
    ...queryOptions
  })
}

export function useProjectPhaseList(projectTypeId, queryOptions) {
  return useQuery({
    queryKey: ["projectPhaseList", { projectTypeId: String(projectTypeId) }],
    queryFn: async () => {
      const projectTypePhaseListCaller = httpsCallable(functions, "getprojecttypephaselist")
      const response = await projectTypePhaseListCaller({ projectTypeId })

      if (!response.data?.items) {
        throw new Error("Failed to fetch project phase list")
      }
      return response.data.items
    },
    // Only run the query if projectTypeId exists
    enabled: !!projectTypeId,
    ...queryOptions
  })
}

export function useProjectAttributes(projectId, queryOptions) {
  return useQuery({
    queryKey: ["projectVitals", { projectId: String(projectId) }],
    queryFn: async () => {
      if (!projectId) {
        return null
      }

      const projectVitalsCaller = httpsCallable(functions, "getprojectvitals")
      const response = await projectVitalsCaller({ projectId })

      if (!response.data) {
        throw new Error("Failed to fetch project attributes")
      }
      return response.data
    },
    // Only run the query if projectId exists
    enabled: !!projectId,
    ...queryOptions
  })
}

export const useTenant = (queryOptions) => {
  return useQuery({
    queryKey: ["userList"],
    queryFn: async () => {
      const userListCaller = httpsCallable(functions, "getuser")
      const response = await userListCaller()
      if (!response.data) {
        throw new Error("Failed to fetch user list")
      }
      return response.data
    },
    ...queryOptions
  })
}

export function useCompleteTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ taskId, projectId }) => {
      const user = auth.currentUser
      if (!user) {
        throw new Error("User not authenticated")
      }

      const idToken = await user.getIdToken()
      const response = await fetch(`${BASE_URL}/completeTask`, {
        method: "POST",
        headers: {
        ...(await appCheckHeaders()),
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ taskId, projectId })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      return response.json()
    },
    onSuccess: (_data, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projectVitals", { projectId: String(projectId) }]
      })
      queryClient.invalidateQueries({
        queryKey: ["projectList"]
      })
    }
  })
}

export function useTaskReportingStatus(queryOptions) {
  return useQuery({
    queryKey: ["taskReportingStatus"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getTaskReportingStatus`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

// The firm's own people for the Tasks charts by person; see getTaskPeople.
export function useTaskPeople(queryOptions) {
  return useQuery({
    queryKey: ["taskPeople"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getTaskPeople`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

export function useEventReportingStatus(queryOptions) {
  return useQuery({
    queryKey: ["eventReportingStatus"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getEventReportingStatus`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

export function useLoisUsageReportingStatus(queryOptions) {
  return useQuery({
    queryKey: ["loisUsageReportingStatus"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getAiUsageReportingStatus`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

export function useFailedDocsReportingStatus(queryOptions) {
  return useQuery({
    queryKey: ["failedDocsReportingStatus"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getFailedDocsReportingStatus`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

export function useMedChronReportingStatus(queryOptions) {
  return useQuery({
    queryKey: ["medChronReportingStatus"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getMedChronReportingStatus`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

export function useAskLoisReportingStatus(queryOptions) {
  return useQuery({
    queryKey: ["askLoisReportingStatus"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getAskLoisReportingStatus`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

export function useUserAuditReportingStatus(queryOptions) {
  return useQuery({
    queryKey: ["userAuditReportingStatus"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getUserAuditReportingStatus`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

/**
 * The caller's tenant timezone configuration, and whether they may change it.
 *
 * `canEdit` is resolved server-side. The UI hides controls a caller cannot use,
 * but the callable is what refuses them.
 */
export function useTenantTimezoneSettings(queryOptions) {
  return useQuery({
    queryKey: ["tenantTimezoneSettings"],
    queryFn: async () => {
      const idToken = await auth.currentUser?.getIdToken()
      if (!idToken) throw new Error("Not authenticated")
      const response = await fetch(`${BASE_URL}/getTenantTimezoneSettings`, {
        method: "GET",
        headers: { ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const json = await response.json()
      if (!response.ok)
        throw new Error(json.error || `Request failed with status ${response.status}`)
      return json.data
    },
    ...queryOptions
  })
}

/**
 * The firm's confirmed zones, for the User Audit page to render each person on
 * their own clock.
 *
 * Absent means empty rather than broken: with nothing set the page reads in UTC
 * exactly as it did before any of this existed, with the fallback stated.
 */
export function useTenantTimezoneMap(queryOptions) {
  return useQuery({
    queryKey: ["tenantTimezoneMap"],
    queryFn: async () => {
      const caller = httpsCallable(functions, "getTenantTimezoneMap")
      const response = await caller()
      return response.data
    },
    // The zones change when somebody confirms one, which is rare, and the audit
    // page reloads a 200,000-row report beside this.
    staleTime: 5 * 60 * 1000,
    ...queryOptions
  })
}

/**
 * Everyone the audit report can carry, and what is known about each one's zone.
 * A firm manager's view; the callable refuses anyone else.
 */
export function useTenantTimezoneRoster(queryOptions) {
  return useQuery({
    queryKey: ["tenantTimezoneRoster"],
    queryFn: async () => {
      const caller = httpsCallable(functions, "getTenantTimezoneRoster")
      const response = await caller()
      return response.data
    },
    ...queryOptions
  })
}

/**
 * The caller's own timezone entry, and what their firm allows them to do about it.
 *
 * The map key is never sent or received as something the client chooses: it is
 * resolved server-side from the caller's own Filevine identity. `username: null`
 * comes back for anyone with no such identity, and hides the control rather than
 * showing one that could never save.
 */
export function useMyTimezone(queryOptions) {
  return useQuery({
    queryKey: ["myTimezone"],
    queryFn: async () => {
      const caller = httpsCallable(functions, "getMyTimezone")
      const response = await caller()
      return response.data
    },
    // Resolving the map key costs a Filevine session and a user-list fetch, and
    // the default staleTime of 0 pays it again every time the tab regains
    // focus. Nothing here changes on its own inside five minutes, and saving
    // invalidates the key anyway, so the refresh that matters still happens.
    staleTime: 5 * 60 * 1000,
    ...queryOptions
  })
}

/**
 * The caller's tenant's Legalware accounts, and which hold the firm-manager role.
 *
 * The tenant is resolved server-side from the caller's record, so this does not
 * wait on anything else to learn which one it is.
 */
export function useTenantAdminCandidates(queryOptions) {
  return useQuery({
    queryKey: ["tenantAdminCandidates"],
    queryFn: async () => {
      const caller = httpsCallable(functions, "getTenantAdminCandidates")
      const response = await caller()
      return response.data
    },
    ...queryOptions
  })
}

export function useCheckPatSecurity(queryOptions) {
  const [user] = useAuthState(auth)
  return useQuery({
    queryKey: ["patSecurity"],
    queryFn: async () => {
      const idToken = await user.getIdToken()
      const response = await fetch(`${BASE_URL}/checkpatsecurity`, {
        method: "GET",
        headers: {
        ...(await appCheckHeaders()), Authorization: `Bearer ${idToken}` }
      })
      const { data } = await response.json()
      return data
    },
    enabled: !!user,
    ...queryOptions
  })
}

export function useIndividualProjectData(projectId, queryOptions = {}) {
  const [user] = useAuthState(auth)
  const { enabled = true, ...restOptions } = queryOptions

  return useQuery({
    queryKey: ["IndividualProjectData", { projectId: String(projectId) }],
    queryFn: async () => {
      if (!projectId) {
        return null
      }

      const idToken = await user.getIdToken()

      // Call the HTTP function
      const response = await fetch(`${BASE_URL}/getSingleProject`, {
        method: "POST",
        headers: {
        ...(await appCheckHeaders()),
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ projectId })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    },
    enabled: !!user && !!projectId && enabled,
    ...restOptions
  })
}

/**
 * How many backend error groups are open: the red dot on the System Errors tab
 * in /legalware-power-user. Polled while that page is open, which only app admins can reach.
 *
 * Never throws to the layout's error boundary (the global default): a failed
 * poll must cost the dot, not the page.
 */
export function useSystemErrorSummary() {
  return useQuery({
    queryKey: ["systemErrorSummary"],
    queryFn: async () => {
      const caller = httpsCallable(functions, "getSystemErrorSummary")
      const response = await caller()
      return response.data
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
    throwOnError: false
  })
}

/**
 * The error groups for /legalware-power-user -> System Errors: every open group
 * (`view` "open"), or the latest in any state ("all").
 */
export function useSystemErrors(view) {
  return useQuery({
    queryKey: ["systemErrors", view],
    queryFn: async () => {
      const caller = httpsCallable(functions, "getSystemErrors")
      const response = await caller({ view })
      return response.data.errors
    },
    throwOnError: false
  })
}

/**
 * Every tenant with its feature flags, for /legalware-power-user -> Feature Flags.
 * The caller's own tenant's flags also come with useTenant, as `featureFlags`.
 */
export function useFeatureFlags() {
  return useQuery({
    queryKey: ["featureFlags"],
    queryFn: async () => {
      const caller = httpsCallable(functions, "getFeatureFlags")
      const response = await caller()
      return response.data.tenants
    },
    throwOnError: false
  })
}
